import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import uiActions from "../../../../../../../redux/ui/action";
import authActions from "../../../../../../../redux/auth/action";
import { connect } from "react-redux";
import { useHistory } from "react-router-dom";
import images from "../../../../../../../utilities/images/images";
import "./PaytmHandbookResponse.scss";
import { url_169, url_302, url_303, url_8 } from "../../../../../../../custom_utilities/api_services";
import {
  clearAwaitPaymentFromUrl,
  clearPendingHandbookOrderId,
  getPendingHandbookOrderId,
  isPaymentCompleted,
  isPaymentStillPending,
  normalizePaymentStatus,
  PAYTM_HANDBOOK_CHECKOUT_FRAME,
  PAYTM_MAX_POLL_MS,
  PAYTM_POLL_INTERVAL_MS,
  shouldFinalizeAfterPaytmReturn,
  shouldFinalizePayment,
  submitHandbookPaytmCheckoutInFrame,
} from "../../../../../../../custom_utilities/paytm_payment/paytmHandbookHelpers";

const PaytmHandbookResponse = (props) => {
  const {
    tokenType,
    token,
    orderId,
    handBookOrderId,
    paytmDetails,
    transactionData,
    startLoading,
    finishLoading,
    setUser,
  } = props;

  const [handbookResponse, setHandbookResponse] = useState({});
  const [showCheckout, setShowCheckout] = useState(false);
  const [showResult, setShowResult] = useState(false);
  const [isPolling, setIsPolling] = useState(false);
  const [isReceiptLoading, setIsReceiptLoading] = useState(false);
  const history = useHistory();
  const pollCleanupRef = useRef(null);
  const checkoutSubmittedRef = useRef(false);
  const iframeLoadCountRef = useRef(0);
  const paymentSettledRef = useRef(false);
  const showResultRef = useRef(false);

  const getOrderIdFromUrl = () => {
    const params = new URLSearchParams(window.location.search);
    return (
      params.get("order_id") ||
      params.get("ORDERID") ||
      params.get("orderId") ||
      getPendingHandbookOrderId() ||
      ""
    );
  };

  const getResolvedOrderId = () =>
    orderId || handBookOrderId || getOrderIdFromUrl();

  const isAwaitingPayment = () => {
    const params = new URLSearchParams(window.location.search);
    return params.get("await_payment") === "1";
  };

  const fetchTransactionStatus = async (currentOrderId) => {
    const headers = {
      Authorization: `${tokenType} ${token}`,
    };

    const response = await axios.get(
      `${url_302}?order_id=${encodeURIComponent(currentOrderId)}`,
      { headers }
    );

    return response.data;
  };

  const showFailureResult = async (responseData = { paymentStatus: "Failed" }) => {
    if (paymentSettledRef.current) return;
    paymentSettledRef.current = true;
    setShowCheckout(false);
    setShowResult(true);
    setHandbookResponse(responseData);
    clearAwaitPaymentFromUrl(history);
    clearPendingHandbookOrderId();
  };

  const handlePaymentResult = async (responseData) => {
    if (paymentSettledRef.current) return;
    paymentSettledRef.current = true;
    setShowCheckout(false);
    setShowResult(true);
    setHandbookResponse(responseData);
    clearAwaitPaymentFromUrl(history);
    clearPendingHandbookOrderId();

    if (isPaymentCompleted(responseData)) {
      await saveUserTransactions();
    }
  };

  const updateUser = async () => {
    startLoading();
    try {
      const response = await axios.get(url_8);
      if (response.data.status === 200) {
        setUser(response.data.data);
        finishLoading();
        return 1;
      }
      finishLoading();
      return 0;
    } catch (error) {
      console.log(error);
      finishLoading();
      return 0;
    }
  };

  const saveUserTransactions = async () => {
    const priceInfo = transactionData?.price_info;
    if (!priceInfo) return;

    const { price, discount, shipping, total_price } = priceInfo;
    const dataDetails = {
      price,
      discount,
      shipping_charge: shipping,
      total_price,
      coupon_discount: 0,
    };

    startLoading();
    try {
      const response = await axios.post(url_169, dataDetails);
      if (response.data.status === 200) {
        finishLoading();
        await updateUser();
      } else {
        finishLoading();
      }
    } catch (error) {
      console.log(error);
      finishLoading();
    }
  };

  const triggerPdfDownload = (blob, fileName) => {
    const objectUrl = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = objectUrl;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(objectUrl);
  };

  const downloadReceipt = async () => {
    const currentOrderId = getResolvedOrderId();
    if (!currentOrderId) {
      alert("Order id not found.");
      return;
    }

    setIsReceiptLoading(true);
    try {
      const response = await axios.get(url_303, {
        params: { order_id: currentOrderId },
        headers: {
          Authorization: `${tokenType} ${token}`,
        },
        responseType: "blob",
      });

      const contentType = response.headers?.["content-type"] || "";

      if (contentType.includes("application/pdf")) {
        triggerPdfDownload(
          new Blob([response.data], { type: "application/pdf" }),
          `receipt-${currentOrderId}.pdf`
        );
        return;
      }

      const responseText = await response.data.text();
      let payload = {};
      try {
        payload = JSON.parse(responseText);
      } catch (parseError) {
        console.log("Error parsing receipt response:", parseError);
      }

      const msg = payload?.message;
      alert(
        typeof msg === "string" && msg
          ? msg
          : "Could not download receipt."
      );
    } catch (error) {
      console.log("Error downloading receipt:", error);
      const errorPayload = error?.response?.data;
      let message = "";

      if (errorPayload instanceof Blob) {
        try {
          const parsed = JSON.parse(await errorPayload.text());
          message = parsed?.message || "";
        } catch (parseError) {
          console.log("Error parsing receipt error response:", parseError);
        }
      } else {
        message = errorPayload?.message || error?.message || "";
      }

      alert(message || "Could not download receipt. Please try again.");
    } finally {
      setIsReceiptLoading(false);
    }
  };

  const getPaytmHandbookStatus = async () => {
    const currentOrderId = getResolvedOrderId();
    if (!currentOrderId) return;

    try {
      const responseData = await fetchTransactionStatus(currentOrderId);

      if (
        responseData.status === 200 &&
        shouldFinalizePayment(responseData)
      ) {
        await handlePaymentResult(responseData);
      } else {
        await showFailureResult(responseData);
      }
    } catch (err) {
      await showFailureResult();
    }
  };

  const stopPolling = () => {
    if (pollCleanupRef.current) {
      pollCleanupRef.current();
      pollCleanupRef.current = null;
    }
  };

  const startPaymentPolling = (currentOrderId) => {
    let elapsed = 0;
    let settled = false;

    const cleanup = () => {
      clearInterval(pollTimer);
      setIsPolling(false);
    };

    const settlePayment = async (responseData) => {
      if (settled) return;
      settled = true;
      cleanup();
      await handlePaymentResult(responseData);
    };

    const pollStatus = async () => {
      try {
        const responseData = await fetchTransactionStatus(currentOrderId);

        if (
          responseData.status === 200 &&
          shouldFinalizePayment(responseData)
        ) {
          await settlePayment(responseData);
          return true;
        }
      } catch (error) {
        console.log("Error polling handbook transaction status:", error);
      }
      return false;
    };

    pollStatus();

    const pollTimer = setInterval(async () => {
      elapsed += PAYTM_POLL_INTERVAL_MS;

      const finalized = await pollStatus();
      if (finalized) return;

      if (elapsed >= PAYTM_MAX_POLL_MS) {
        settled = true;
        cleanup();
        await showFailureResult();
      }
    }, PAYTM_POLL_INTERVAL_MS);

    pollCleanupRef.current = cleanup;
  };

  const finalizeAfterPaytmReturn = async () => {
    if (paymentSettledRef.current) return;

    setShowCheckout(false);
    setIsPolling(true);

    const currentOrderId = getResolvedOrderId();
    if (!currentOrderId) {
      await showFailureResult();
      return;
    }

    const maxRetries = 8;
    const retryDelayMs = 2000;
    let pendingFailedStreak = 0;

    for (let attempt = 0; attempt < maxRetries; attempt += 1) {
      try {
        const responseData = await fetchTransactionStatus(currentOrderId);
        if (responseData.status === 200) {
          if (isPaymentStillPending(responseData)) {
            pendingFailedStreak += 1;
          } else {
            pendingFailedStreak = 0;
          }

          if (shouldFinalizeAfterPaytmReturn(responseData, pendingFailedStreak)) {
            stopPolling();
            await handlePaymentResult(responseData);
            return;
          }
        }
      } catch (error) {
        console.log("Error checking status after Paytm redirect:", error);
      }

      if (attempt < maxRetries - 1) {
        await new Promise((resolve) => setTimeout(resolve, retryDelayMs));
      }
    }

    stopPolling();
    await showFailureResult();
  };

  const handleIframeLoad = async () => {
    iframeLoadCountRef.current += 1;
    if (iframeLoadCountRef.current === 1) return;
    await finalizeAfterPaytmReturn();
  };

  useEffect(() => {
    const currentOrderId = getResolvedOrderId();
    if (!currentOrderId) return;

    if (isAwaitingPayment() && Object.keys(paytmDetails || {}).length) {
      setShowCheckout(true);
      setIsPolling(true);
      startPaymentPolling(currentOrderId);
    } else {
      getPaytmHandbookStatus();
    }

    return () => {
      stopPolling();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!showCheckout || checkoutSubmittedRef.current) return;
    if (!Object.keys(paytmDetails || {}).length) return;

    const timer = setTimeout(() => {
      const submitted = submitHandbookPaytmCheckoutInFrame(paytmDetails);
      checkoutSubmittedRef.current = submitted;
      if (!submitted) {
        showFailureResult();
      }
    }, 100);

    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showCheckout, paytmDetails]);

  useEffect(() => {
    showResultRef.current = Boolean(showResult);
  }, [showResult]);

  useEffect(() => {
    const onPopState = () => {
      if (!showResultRef.current) return;
      history.replace({
        pathname: "/store",
        state: { tab: "hand_books" },
      });
    };

    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const goToStore = () => {
    history.push({
      pathname: "/store",
      state: { tab: "hand_books" },
    });
  };

  const goToMyPackages = () => {
    history.push({
      pathname: "/user_edit_profile",
      state: { tab: "packages" },
    });
  };

  const renderResult = () => {
    if (isPaymentCompleted(handbookResponse)) {
      return (
        <div className="paytm_handbook_success_wrapper">
          <img src={images.handbookOrderSuccess} alt="order-success-img" />
          <div className="order_status">Order Successful!</div>
          <div className="order_id">
            Order Id : <span> {getResolvedOrderId()} </span>
          </div>
          <div className="order_text">
            Order has been placed successfully. will be sharing the dispatch
            details soon.
          </div>
          <button
            type="button"
            onClick={downloadReceipt}
            disabled={isReceiptLoading}
            style={{
              marginTop: "20px",
              width: "100%",
              padding: "12px 24px",
              background: "#263238",
              color: "#fff",
              border: "none",
              borderRadius: "6px",
              fontFamily: "Poppins, sans-serif",
              fontSize: "14px",
              fontWeight: 600,
              cursor: isReceiptLoading ? "not-allowed" : "pointer",
            }}
          >
            {isReceiptLoading ? "Downloading..." : "Download Receipt"}
          </button>
          <div className="go_to_store" onClick={goToStore}>
            Go to store
          </div>
          <div className="check_my_pkgs_btn" onClick={goToMyPackages}>
            <button>Check My packages</button>
          </div>
          <div className="check_my_pkgs_text">
            You can check your order details in the my package section
          </div>
        </div>
      );
    }

    return (
      <div className="paytm_handbook_failed_wrapper">
        <img src={images.handbookOrderFailed} alt="order-failed-img" />
        <div className="on_no_text">Oh No!</div>
        <div className="order_failed_status">Payment Failed</div>
        <div className="order_failed_text">
          We can’t process your request now, please try again after sometime.
        </div>
        <div className="go_to_store" onClick={goToStore}>
          <button>Go to Store</button>
        </div>
      </div>
    );
  };

  if (showCheckout) {
    return (
      <iframe
        name={PAYTM_HANDBOOK_CHECKOUT_FRAME}
        title="Paytm Secure Payment"
        onLoad={handleIframeLoad}
        style={{
          height: "100vh",
          width: "100%",
          border: "none",
        }}
      />
    );
  }

  return (
    <div
      style={{
        height: "100vh",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
      }}
    >
      <div style={{ width: "500px" }} className="paytm_handbook_container">
        {showResult && Object.keys(handbookResponse).length ? renderResult() : null}
        {!showResult && !showCheckout && isPolling ? (
          <div
            style={{
              fontFamily: "Poppins, sans-serif",
              fontSize: "16px",
              color: "#263238",
              textAlign: "center",
            }}
          >
            Checking payment status...
          </div>
        ) : null}
      </div>
    </div>
  );
};

const mapStateToProps = (state) => ({
  orderId: state.paymentReducer.orderId,
  handBookOrderId: state.handBookPaymentData.handBookOrderId,
  userData: state.auth.user,
  token: state.auth.authToken,
  tokenType: state.auth.authType,
  paytmDetails: state.paymentReducer.paytmDetails,
  transactionData: state.handBookPaymentData.handBookTransactionDetails,
});

const mapDispatchToProps = (dispatch) => ({
  startLoading: () => {
    dispatch(uiActions.startLoading());
  },
  finishLoading: () => {
    dispatch(uiActions.finishLoading());
  },
  setUser: (payload) => {
    dispatch(authActions.setUser(payload));
  },
});

export default connect(mapStateToProps, mapDispatchToProps)(PaytmHandbookResponse);
